import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Divider, FormControl, Grid, Box, FormControlLabel, styled, Switch, switchClasses, Typography, Accordion, AccordionSummary, AccordionDetails, TableHead, TableRow, TableCell, TableBody, Table, IconButton, Button } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomRadioButton from "../../common/CustomRadioButton";
import CustomAddressTextField from "../../common/CustomAddressTextField";
import {
  CodeTable,
  fetchAllCodeTablesValues,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import {
  PracticeInfo,
  fetchProducts,
  usePracticeRegistrationSlice,
} from "../../../redux/slice/practice/practiceRegistrationSlice";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ArrowBack, Close, ExpandMore, NoteAddOutlined } from "@mui/icons-material";
import CustomTable from '../../common/CustomTable';
import { tokens } from '../../../theme';
import { Column } from "../../../redux/slice/commonSlice";
import { AuthorizedSignerInfo } from "./AuthorizedSignerPopup";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { USER_ROLE_ENTITY_NAMES, PRODUCT_CATEGORY_CODETABLE } from "../../../constants/applicationConstants";
import { ProductCommissionAgency } from "./PracticeEditView";
import { CreateSearchAgency, useAgencyReducer } from "../../../redux/slice/agency/AgencySlice";
import moment from "moment";
import { Row } from "../agency/AgencyForm";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomCheckboxGroup from "../../common/CustomCheckboxGroup";
import CustomNumberField from "../../common/CustomNumberField";
import { useModalStyles } from "../../common/useModalStyles";

export const SwitchTextTrack = styled(Switch)({
  width: 74,
  height: 42,
  padding: 8,
  [`& .${switchClasses.switchBase}`]: {
    padding: 11,
    color: "#ff6a00",
  },
  [`& .${switchClasses.thumb}`]: {
    width: 20,
    height: 20,
    backgroundColor: "#fff",
  },
  [`& .${switchClasses.track}`]: {
    background:
      "linear-gradient(to right,rgb(161, 161, 161),rgb(84, 84, 84))",
    opacity: "1 !important",
    borderRadius: 20,
    position: "relative",
    "&:before, &:after": {
      display: "inline-block",
      position: "absolute",
      top: "50%",
      width: "50%",
      transform: "translateY(-50%)",
      color: "#fff",
      textAlign: "center",
      fontSize: "0.75rem",
      fontWeight: 500,
    },
    "&:before": {
      content: '"ON"',
      left: 4,
      opacity: 0,
    },
    "&:after": {
      content: '"OFF"',
      right: 4,
    },
  },
  [`& .${switchClasses.checked}`]: {
    [`&.${switchClasses.switchBase}`]: {
      color: "#185a9d",
      transform: "translateX(32px)",
      "&:hover": {
        backgroundColor: "rgba(24,90,257,0.08)",
      },
    },
    [`& .${switchClasses.thumb}`]: {
      backgroundColor: "#fff",
    },
    [`& + .${switchClasses.track}`]: {
      background: "linear-gradient(to right,rgb(0, 196, 88),rgb(0, 97, 29))",
      "&:before": {
        opacity: 1,
      },
      "&:after": {
        opacity: 0,
      },
    },
  },
  [`& .${switchClasses.disabled}`]: {
    [`&.${switchClasses.switchBase}`]: {
      color: "rgb(84, 84, 84) !important",
    },
    [`& .${switchClasses.thumb}`]: {
      backgroundColor: "#fafafa !important",
    },
    [`& + .${switchClasses.track}`]: {
      background: "rgb(84, 84, 84) !important",
      "&:before, &:after": {
        color: "#fff !important",
      },
    },
  },
  [`& .${switchClasses.checked}.${switchClasses.disabled}`]: {
    [`&.${switchClasses.switchBase}`]: {
      color: "rgb(84, 84, 84) !important",
    },
    [`& + .${switchClasses.track}`]: {
      background: "rgb(84, 84, 84) !important",
      "&:before": {
        opacity: 1,
        color: "#fff !important",
      },
      "&:after": {
        opacity: 0,
      },
    },
  },
});

interface ApprovePracticeProps {
  open: boolean;
  onClose: () => void;
  practiceInfo?: PracticeInfo;
}

  const signerColumns: Column[] = [
    { id: "fullName", label: "Full Name", minWidth: 200 },
    { id: "email", label: "Email", minWidth: 100 },
    { id: "isOwner", label: "Ownership", minWidth: 100 },
    { id: "position", label: "Position", minWidth: 100 },
  ];

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

  const overrideEntityColumns: Column[] = [
    { id: "productName", label: "Product", minWidth: 100 },
    { id: "effectiveDate", label: "Effective Date", minWidth: 100 },
    { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
    { id: "overrideAgency", label: "Override Agency", minWidth: 100 },
    { id: "commissionRate", label: "Service Fee", minWidth: 100 },
    { id: "associateEntityName", label: "Associate Entity", minWidth: 100 },
    { id: "associateAgencyCommission", label: "Associate Entity Svc Fee", minWidth: 100, format: (value: number) => { return value + " %" } },
  ];

const PracticeApprovePopup: React.FC<ApprovePracticeProps> = ({
  open,
  onClose,
}) => {
  const { control, reset, getValues, watch, setValue } = useForm<PracticeInfo>();
  const { practiceEditInfo } = usePracticeEditSlice();
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [woundCareOptions, setWoundCareOptions] = React.useState<CodeTable[]>(
    []
  );
    //CS-1242 - Start - Don't remove this code
    const [practiceSpecializationList, setPracticeSpecializationList] = React.useState<
    CodeTable[]
    >([]);
    //CS-1242 - End
  const [placeOfService, setPlaceOfService] = React.useState<CodeTable[]>([]);
  const [placesOfServiceCheck, setPlacesOfServiceCheck] = React.useState<string[]>([]);
  const theme = useTheme();
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    inputFieldSx,
    cancelBtnSx,
  } = useModalStyles();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const betweenSmAndMd = useMediaQuery(
    `(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`
  );
  const colors = tokens(theme.palette.mode);
  const [signersRows, setSignersRows] = React.useState<{ fullName: string, email: string }[]>([]);
  const { userInfo } = useAuthSlice();
  const [productCategories, setProductCategories] = React.useState<CodeTable[]>([]);
  const { overrideAgencyCommissionsList, associateAgencyList, agencyExecutives, practiceByUserRoleAgencySlice } = useAgencyReducer();
  const [eMRIntegrationProviderList, setEMRIntegrationProviderList] = useState<CodeTable[]>([]); 	//CS-1243

  const defaultValues = useCallback(() => {
    if (practiceEditInfo) {
      reset(practiceEditInfo);
    }
  }, [practiceEditInfo, reset]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  useEffect(() => {
    if (practiceEditInfo) {
      const repValue = getValues(`practiceRequestInfo.repOrDistributorName`)?.trim();
      const questionsValue = getValues(`practiceRequestInfo.anyQuestions`)?.trim();
      
      if (!repValue) {
        setValue(`practiceRequestInfo.repOrDistributorName`, "No Representative or Distributor provided.");
      }
      if (!questionsValue) {
        setValue(`practiceRequestInfo.anyQuestions`, "NA");
      }
    }
  }, [practiceEditInfo, getValues, setValue]);

  useEffect(() => {
    if (practiceEditInfo) {
      const repValue = getValues(`practiceRequestInfo.repOrDistributorName`)?.trim();
      const questionsValue = getValues(`practiceRequestInfo.anyQuestions`)?.trim();
      
      if (!repValue) {
        setValue(`practiceRequestInfo.repOrDistributorName`, "No Representative or Distributor provided.");
      }
      if (!questionsValue) {
        setValue(`practiceRequestInfo.anyQuestions`, "NA");
      }
    }
  }, [practiceEditInfo, getValues, setValue]);

  useEffect(() => {
    dispatch(fetchAllCodeTablesValues());
    console.log(allCodeTablesValuesMap);
    setSigners(practiceEditInfo);
  }, [practiceEditInfo]);
  React.useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      console.log(woundCareOptions);
      setWoundCareOptions(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundCare")
      );
      setPlaceOfService(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PlaceOfService")
      );
      //CS-1242 - Start
      setPracticeSpecializationList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PracticeSpecializations")
      );
      //CS-1242 - End
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(()=>{
    if(practiceEditInfo){
      setPlacesOfServiceCheck(getValues("practiceRequestInfo.placesOfService"));
      console.log(getValues("practiceRequestInfo.placesOfService"));
    }
  },[practiceEditInfo, allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedEMRIntegrationProviderList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "EMRIntegrationProvider"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setEMRIntegrationProviderList(sortedEMRIntegrationProviderList);
	// CS-1289 START
      const sortedProductCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductCategories(sortedProductCategoryList);

    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

    const productCategoryOptions = useMemo(() => {
      return productCategories.map(item => ({
          label: item.displayValue,
          value: item.keyName
      }));
  }, [productCategories]);
	// CS-1289 END
  const EMRIntegrationProviderListOptions = useMemo(() => {
    return eMRIntegrationProviderList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [eMRIntegrationProviderList]);

  const setSigners = (practiceEditInfo: any) => {
    const signersArr: { fullName: string, email: string , isOwner: string , position : string | undefined }[] = [];
    practiceEditInfo && practiceEditInfo?.practiceRequestInfo?.authorizedSigners?.forEach((signer: AuthorizedSignerInfo) => {
      signersArr.push({
        fullName: signer.title ? `${signer.title} ${signer.fullName}` : signer.fullName,//CS-1425 
        email: signer.email,
        isOwner: signer.isOwner,
        position: signer.position

      });
    });
    setSignersRows(signersArr);
  };

  //CS-1234 - Start
  const findProductCommission = (commission: ProductCommissionAgency) => {
    if (associateAgencyList && associateAgencyList.length > 0) {
      const associateAgency: CreateSearchAgency | undefined = associateAgencyList?.find((associateAgency: CreateSearchAgency) => commission.associateAgencyId == associateAgency.agencyId);
      if (associateAgency && associateAgency.agencyProductCommisions) {
        const associateProdComm: ProductCommissionAgency | undefined = associateAgency.agencyProductCommisions?.find((prodComm: ProductCommissionAgency) => prodComm.productId === commission.productId);
        if (associateProdComm && associateProdComm.commissionRate) {
          return associateProdComm.commissionRate;
        }
      }
    }
    return "0%";
  }

  const commissionRow: Row[] = useMemo(() => {
    let commission: Row[] = [];
    if (overrideAgencyCommissionsList) {
      let rowIndex = 0;

      overrideAgencyCommissionsList.forEach((commissionItem: ProductCommissionAgency) => {
        commission.push({
          productCommissionId: commissionItem.productCommissionId,
          productId: commissionItem.productId,
          productName: commissionItem.productName,
          associateType: commissionItem.associateType,
          associateAgencyId: commissionItem.associateAgencyId,
          associatePracticeId: commissionItem.associatePracticeId,
          associateAccountRepId: commissionItem.associateAccountRepId,
          commissionRate: commissionItem.commissionRate + " %",
          commissionPayTerms: commissionItem.commissionPayTerms,
          associateAgencyName: commissionItem.associateAgencyName,
          associateAccountRepName: commissionItem.associateAccountRepName,
          associatePracticeName: commissionItem.associatePracticeName,
          associateEntityName: commissionItem.associateAgencyName || commissionItem.associateAccountRepName || commissionItem.associatePracticeName,
          associateAgencyCommission: commissionItem.associateAgencyId ? findProductCommission(commissionItem) : "",
          overrideAgency: commissionItem.agencyName,
          effectiveDate: commissionItem.effectiveDate ? moment(commissionItem.effectiveDate?.toString())?.format("MM/DD/YYYY") : "",
          inactiveDate: commissionItem.inactiveDate ? moment(commissionItem.inactiveDate?.toString())?.format("MM/DD/YYYY") : "",
          rowIndex: rowIndex++,
        });
      });
    }
    return commission;
  }, [overrideAgencyCommissionsList, associateAgencyList]);
  //CS-1234 - End
  //CS-1395 - Start
  const groups = (practiceEditInfo?.authorizedUserList || []).map((p: any) => ({
    providerId: p.providerId,
    providerName: p.providerName || 'All Provider',
    providerRaw: p, // keep the raw provider if needed for navigation
    users: (p.users || []).map((u: any) => ({
      email: u.email,
      userRole: u.userRole,
      addedBy: u.addedBy,
      addedOn: u.addedOn,
      isNotificationEnabled: u.isNotificationEnabled,
      providerAuthorizedUserId: u.providerAuthorizedUserId,
      userId: u.userId
    })),
  }));
  //CS-1395 - End

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: modalTokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: modalTokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const SectionHeader = ({ title }: { title: string }) => (
    <Typography
      sx={{
        fontSize: "0.95rem",
        fontWeight: 700,
        color: modalTokens.textPrimary,
        lineHeight: 1.35,
        mb: 1.25,
      }}
    >
      {title}
    </Typography>
  );

  const LabeledTextField = ({
    fieldLabel,
    required,
    ...props
  }: any) => (
    <Box>
      <FieldLabel label={fieldLabel} required={required} />
      <Box sx={inputFieldSx(false)}>
        <CustomTextField {...props} label="" size={props.size ?? "small"} />
      </Box>
    </Box>
  );

  const LabeledPatternTextField = ({
    fieldLabel,
    required,
    ...props
  }: any) => (
    <Box>
      <FieldLabel label={fieldLabel} required={required} />
      <Box sx={inputFieldSx(false)}>
        <CustomPatternTextField {...props} label="" size={props.size ?? "small"} />
      </Box>
    </Box>
  );

  const LabeledDropdownField = ({
    fieldLabel,
    required,
    ...props
  }: any) => (
    <Box>
      <FieldLabel label={fieldLabel} required={required} />
      <Box sx={dropdownWrapSx(false)}>
        <CustomDropdownField {...props} label="" size={props.size ?? "small"} />
      </Box>
    </Box>
  );

  const LabeledNumberField = ({
    fieldLabel,
    required,
    ...props
  }: any) => (
    <Box>
      <FieldLabel label={fieldLabel} required={required} />
      <Box sx={inputFieldSx(false)}>
        <CustomNumberField {...props} label="" size={props.size ?? "small"} />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
      // PaperProps={{
      //   style: {
      //     maxWidth: isXsScreen ? '65%' : '1400px', width: '65%',
      //     marginLeft: isXsScreen ? '40%' : '20%',
      //   },
      // }}
      maxWidth="lg"
      fullWidth
      fullScreen={isXsScreen}
      PaperProps={{
        sx: {
          ...paperSx(isXsScreen),
          width: isXsScreen ? "100%" : "min(1180px, calc(100vw - 64px))",
        },
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onClose={onClose}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <NoteAddOutlined sx={{ color: modalTokens.accent, fontSize: "1.25rem" }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: modalTokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Practice Request
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: modalTokens.textSecondary,
              mt: 0.15,
            }}
          >
            Review the submitted practice details below
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <Close sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          ...bodySx,
          gap: 0,
          px: { xs: 2, sm: 2.8 },
          py: 2.4,
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
            borderRadius: 4,
          },
          "& .MuiTypography-h3": {
            fontSize: "0.95rem",
            fontWeight: 700,
            color: modalTokens.textPrimary,
            lineHeight: 1.4,
          },
          "& .MuiTypography-h5": {
            fontSize: "0.8rem",
            color: modalTokens.textSecondary,
          },
          "& label, & .MuiFormControlLabel-label": {
            color: modalTokens.textSecondary,
            fontSize: "0.8rem",
            fontWeight: 600,
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: `${modalTokens.textPrimary} !important`,
            opacity: 0.72,
          },
          "& .MuiInputLabel-root.Mui-disabled": {
            color: `${modalTokens.textSecondary} !important`,
          },
          "& .MuiCheckbox-root.Mui-disabled, & .MuiSwitch-root.Mui-disabled": {
            opacity: 0.65,
          },
          "& .MuiAccordion-root": {
            backgroundColor: modalTokens.cardBg,
            backgroundImage: "none",
            borderColor: `${modalTokens.border} !important`,
            borderRadius: "10px !important",
            overflow: "hidden",
          },
          "& .MuiAccordionSummary-root": {
            color: modalTokens.textPrimary,
          },
          "& .MuiTable-root": {
            backgroundColor: `${modalTokens.inputBg} !important`,
          },
          "& .MuiTableCell-root": {
            borderColor: modalTokens.border,
            color: modalTokens.textPrimary,
          },
          "& .MuiTableCell-head": {
            backgroundColor: `${modalTokens.inputBg} !important`,
          },
        }}
      >
        <SectionHeader title="Practice Information" />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              fieldLabel="Practice Name"
              control={control}
              controllerName="practiceName"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              fieldLabel="Practice Tax ID"
              control={control}
              controllerName="taxId"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              fieldLabel="Practice NPI"
              control={control}
              controllerName="npi"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              fieldLabel="Practice PTAN"
              control={control}
              controllerName="ptan"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Contact First Name"
                  control={control}
                  controllerName="contactFirstName"
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Contact Middle Name"
                  control={control}
                  controllerName="contactMiddleName"
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Contact Last Name"
                  control={control}
                  controllerName="contactLastName"
                  isDisabled={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledTextField
              fieldLabel="Contact Email"
              control={control}
              controllerName="contactEmail"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledPatternTextField
              fieldLabel="Contact Phone"
              control={control}
              name="contactPhone"
              value={practiceEditInfo?.contactPhone || ""}
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledPatternTextField
              fieldLabel="Contact Fax"
              control={control}
              name="contactFax"
              value={practiceEditInfo?.contactFax || ""}
              isDisabled={true}
            />
          </Grid>
        </Grid>
        <div style={{ height: "10px" }}></div>
        {/* CS-1242 - start */}
        {/* CS-1332 - Start */}
          <>
            <Box sx={{ mt: 2 }}>
            <SectionHeader title="Auto - Eligibility" />
            </Box>
            <Grid container spacing={2} mb={1}>
              <Grid item md={4} xs={12}>
                <Controller
                  name="isAutoIVREnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <SwitchTextTrack
                          {...field}
                          name={field.name}
                          checked={field.value}
                          size="medium"
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            field.onChange(isChecked);
                          }}
                        />
                      }
                      disabled={true}
                      label="Auto-Eligibility-Service (pVerify)" //CS-1369 - Label changes
                      labelPlacement="start"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        {/* CS-1242 - end */}
        {/* CS-1243 - start */}
          <>
            <SectionHeader title="EMR Integration" />
            <Grid container spacing={5} mb={2}>
              <Grid item md={4} xs={12}>
                <Controller
                  name="isEMREnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <SwitchTextTrack
                          {...field}
                          name={field.name}
                          checked={field.value}
                          size="medium"
                          disabled={true}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            field.onChange(isChecked);
                          }}
                        />
                      }
                      disabled={true}
                      label="EMR Integration Service"
                      labelPlacement="start"
                    />
                  )}
                />
              </Grid>
            </Grid>

            {watch("isEMREnabled") && (
              <Grid container spacing={2} mt={1}>
                <Grid item md={4} xs={12}>
                  <LabeledDropdownField
                    fieldLabel="EMR Integration Provider"
                    name="emrIntegrationProvider"
                    control={control}
                    options={EMRIntegrationProviderListOptions}
                    disabled={true}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <LabeledTextField
                    fieldLabel="EMR Provider Practice ID"
                    control={control}
                    controllerName="emrProviderPracticeId"
                    disabled={true}
                  />
                </Grid>
              </Grid>
            )}
        </>
        {/* CS-1332 - End */}
        {/* CS-1243 - end */}

        <div style={{ height: "10px" }}></div>
        <SectionHeader title="Account Payable Information" />
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="AP Contact First Name"
                  control={control}
                  controllerName="apFirstName"
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="AP Contact Middle Name"
                  control={control}
                  controllerName="apMiddleName"
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="AP Contact Last Name"
                  control={control}
                  controllerName="apLastName"
                  isDisabled={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledPatternTextField
              fieldLabel="AP Contact Phone"
              control={control}
              name="apPhone"
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledTextField
              fieldLabel="AP Contact Email"
              control={control}
              controllerName="apEmail"
              isDisabled={true}
            />
          </Grid>
        </Grid>
        <div style={{ height: "10px" }}></div>
        <Box sx={{ mt: 2 }}>
        <SectionHeader title="Biller Information" />
        </Box>
        <Grid container spacing={2}>
          {/* <Grid item md={12}>
            <CustomCheckbox
              id={"12"}
              control={control}
              controllerName={`practiceRequestInfo.${"billerNotApplicable"}`}
              label="Not Applicable"
              checked={billerNotApplicableCheck}
              isDisabled={true}
            />
          </Grid> */}
          <Grid item xs={12} md={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Biller First Name"
                  control={control}
                  controllerName={`practiceRequestInfo.${"billerFirstName"}`}
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Biller Middle Name"
                  control={control}
                  controllerName={`practiceRequestInfo.${"billerMiddleName"}`}
                  isDisabled={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LabeledTextField
                  fieldLabel="Biller Last Name"
                  control={control}
                  controllerName={`practiceRequestInfo.${"billerLastName"}`}
                  isDisabled={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledPatternTextField
              fieldLabel="Biller Contact Phone"
              control={control}
              name={`practiceRequestInfo.${"billerContactPhone"}`}
              isDisabled={true}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledTextField
              fieldLabel="Biller Contact Email"
              control={control}
              controllerName={`practiceRequestInfo.${"billerContactEmail"}`}
              isDisabled={true}
            />
          </Grid>
          {/* <Grid item md={12}>
            <CustomAddressTextField
              control={control}
              context="Biller"
              name="practiceRequestInfo"
              isDisabled={true}
            />
          </Grid> */}
        </Grid>
        <div style={{ height: "10px" }}></div>

        {/* CS-1234 - Start */}
        {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
          <Box mt="5px">
            <Box sx={{ mt: 2 }}>
            <SectionHeader title="Override Entity Information" />
            </Box>
            <Grid container spacing={1}>
              <Grid item md={12} xs={12}>
                <Box sx={{ backgroundColor: modalTokens.cardBg, borderRadius: 1.5, border: `1px solid ${modalTokens.border}`, p: 1 }} >
                  <CustomTable
                    columns={overrideEntityColumns}
                    rows={commissionRow ? commissionRow : []}
                    disablePagination={true}
                    disabled={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        {/* CS-1234 - End */}

        <div style={{ height: "10px" }}></div>
        <Box sx={{ mt: 2 }}>
        <SectionHeader title="Authorized Signer(s) Information" />
        </Box>
        <Grid container spacing={2}>
          <Grid item md={12} xs={12}>
            <Box sx={{ backgroundColor: modalTokens.cardBg, borderRadius: 1.5, border: `1px solid ${modalTokens.border}`, p: 1 }} >
              <CustomTable
                columns={signerColumns}
                rows={signersRows}
                disablePagination={true}
                disabled={true} />
            </Box>
          </Grid>
        </Grid>
        {groups?.length > 0 && (<>
          <div style={{ height: "10px" }}></div>
            <SectionHeader title="Authorized User To Sign On Provider’s Behalf" />          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 0 }}>
                {groups?.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      px: 2,
                      textAlign: 'center',
                      backgroundColor: modalTokens.cardBg,
                      borderRadius: 1,
                      border: `1px solid ${modalTokens.border}`
                    }}
                  >
                    <Typography variant="h4">
                      No data found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {groups.map((g: any) => (
                      <Accordion key={g.providerId} defaultExpanded disableGutters square sx={{ bgcolor: 'transparent', mt: 1, mb: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>{g.providerName}</Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: { xs: 'normal', md: 'nowrap' } }}>
                              {g.users.length} Authorized Users
                            </Typography>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                          <Box sx={{ overflowX: 'auto' }}>
                            <Table stickyHeader style={{ minWidth: 720 }} >
                              <TableHead>
                                <TableRow sx={{
                                  '& th': {
                                    backgroundColor: theme.palette.mode === "dark" ? "#383838" : colors.grey[800],
                                    color: '#a5a1a1f7',
                                  },
                                }}>
                                  <TableCell sx={{ width: { xs: '50%', md: '40%' }, fontWeight: 'bold', fontSize: '0.8rem' }}>
                                    Authorized User
                                  </TableCell>
                                  <TableCell sx={{ width: { xs: '22%', md: '15%' }, fontWeight: 'bold', fontSize: '0.8rem' }}>
                                    Email Notification
                                  </TableCell>
                                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: '15%', fontWeight: 'bold', fontSize: '0.8rem' }}>
                                    Added/Modified By
                                  </TableCell>
                                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: '20%', fontWeight: 'bold', fontSize: '0.8rem' }}>
                                    Added/Modified At
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {g.users.map((u: any, idx: number) => (
                                  <TableRow key={`${g.providerId}-${idx}`}>
                                    <TableCell>
                                      <Typography>{u.email} ({u.userRole})</Typography>
                                      {/* Show hidden meta stacked under user on small screens */}
                                      <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">By: {u.addedBy}</Typography><br />
                                        <Typography variant="caption" color="text.secondary">At: {u.addedOn}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>
                                        {u.isNotificationEnabled ? 'Yes' : 'No'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{u.addedBy}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{u.addedOn}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}

              </Box>
            </Grid>
          </Grid>
        </>)}
        <div style={{ height: "10px" }}></div>
         {/* <Header
          title="Wound Care Information"
          subtitle=""
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <div style={{ fontSize: "medium" }}>Wound Care Focus</div>
            <FormControl>
              <CustomRadioButton
                control={control}
                controllerName={`practiceRequestInfo.${"woundCareFocus"}`}
                options={
                  woundCareOptions
                    ? woundCareOptions.map((option) => ({
                        value: option.keyName,
                        label: option.displayValue,
                      }))
                    : []
                }
                isDisabled={true}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              control={control}
              controllerName={`practiceRequestInfo.${"averageWoundsTreatedPerMonth"}`}
              label="Average Wounds Treated Per Month"
              isDisabled={true}
            />
          </Grid>
          <Grid item md={4}></Grid> */}
        {/* CS-1242 - Start */}
          
        <Box sx={{ mt: 2 }}>
        <SectionHeader title="Specializations" />
        </Box>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <LabeledDropdownField
              fieldLabel="Type of Specialization(s)"
              control={control}
              name={"specialization"}
              options={
                practiceSpecializationList
                  ? practiceSpecializationList.map((option) => ({
                    value: option.keyName,
                    label: option.displayValue,
                  }))
                  : []
              }
              // rules={{ required: "Type of Specialization(s) is required." }}
              // onChangeSelect={(e) => {
              //   checkProductInfoValidity({ ...watch() });
              // }}
              disabled={true}
              sortOptions={false}
            />
          </Grid>
          <Grid item md={8} xs={12}></Grid>
          {watch("specialization") !== undefined && watch("specialization") !== "" && watch("specialization") !== "Surgical Only" && (<>
            <Grid item md={4} xs={12}>
              <LabeledDropdownField
                fieldLabel="Wound Care Focus"
                control={control}
                name={`practiceRequestInfo.${"woundCareFocus"}`}
                options={
                  woundCareOptions
                    ? woundCareOptions.map((option) => ({
                      value: option.keyName,
                      label: option.displayValue,
                    }))
                    : []
                }
                // rules={{ required: "Wound Care Focus is required." }}
                // onChangeSelect={(e) => {
                //   checkProductInfoValidity({ ...watch() });
                // }}
                disabled={true}
                sortOptions={false}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <LabeledTextField
                fieldLabel="Average Wounds Treated Per Month"
                control={control}
                controllerName={`practiceRequestInfo.${"averageWoundsTreatedPerMonth"}`}
                isDisabled={true}
              />
            </Grid>
            <Grid item md={4}></Grid>
          </>)}
          {/* CS-1242 - End */}
          {/* CS-1248 - Start */}
        <Grid item md={2} xs={12}>
          <Controller
            name="isClaimSupported"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <SwitchTextTrack
                    {...field}
                    name={field.name}
                    checked={field.value}
                    disabled={true}
                    size="medium"
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      field.onChange(isChecked);
                    }}
                  />
                }
                label="Claim Support"
                labelPlacement="start"
              />
            )}
          />
        </Grid>
         {watch("isClaimSupported") &&
        <Grid item md={4} xs={12}>
          <LabeledNumberField
              fieldLabel="Add Claim Due Days"
              control={control}
              controllerName={"claimDueDays"}
              disabled={true}
            />
        </Grid>
        }
          <Grid item md={4}></Grid>
          <Grid item md={12}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{
                  display: {
                    xs: "block",
                    md: "flex",
                  },
                  alignItems: {
                    md: "center",
                  },
                }}
              >
                {/* CS-1289 */}
                <Box sx={{ mr: 2 }}>
                  <FieldLabel label="Product Category" />
                </Box>
                <CustomCheckboxGroup
                  control={control}
                  controllerName="practiceRequestInfo.interestedProductCategory"
                  options={productCategoryOptions}
                  isDisabled={true}
                // rules={{ required: "Please select the option" }}
                // onChange={(e) => handleChange("dmeLicense", e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* CS-1248 - End */}
          <Grid item xs={12} md={4}>
            <FieldLabel label="Place(s) of Service" />
            <FormControl>
              <Grid
                container
                spacing={1}
                style={{ position: "relative", left: "10px" }}
              >
                {(placeOfService || []).map((option, index) => (
                  <Grid item xs={6} key={index}>
                    <CustomCheckbox
                      id={`${index}`}
                      control={control}
                      controllerName={`practiceRequestInfo.${"placeOfService"}`}
                      label={option.displayValue}
                      checked={
                        placesOfServiceCheck &&
                        placesOfServiceCheck.includes(option.keyName)
                      }
                      isDisabled={true}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormControl>
          </Grid>
        </Grid>

        <div style={{ height: "10px" }}></div>
        <Grid container spacing={2}></Grid>
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Divider
              orientation="horizontal"
              variant="middle"
              flexItem
              sx={{ padding: 0, margin: "10px 0" }}
            />
          </Grid>
          <Grid container item spacing={2}>
            <Grid item md={6} xs={12}>
              <LabeledTextField
                fieldLabel="Please provide the name of the Rep or Distributor that brought you here"
                control={control}
                controllerName={`practiceRequestInfo.${"repOrDistributorName"}`}
                multiline
                rows={4}
                maxRows={4}
                disabled={true}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <LabeledTextField
                fieldLabel="Please List Any Questions You Have"
                control={control}
                controllerName={`practiceRequestInfo.${"anyQuestions"}`}
                multiline
                rows={4}
                maxRows={4}
                disabled={true}
              />
            </Grid>
          </Grid>
        </Grid>
{/* 
        <div style={{ height: "30px" }}></div>
        <Header title="Insurance Payer Mix" subtitle="" />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CustomTextField
              control={control}
              controllerName={`practiceRequestInfo.${"insurancePayer1"}`}
              label="#1 Insurance Payer"
              isDisabled={true}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              control={control}
              controllerName={`practiceRequestInfo.${"insurancePayer2"}`}
              label="#2 Insurance Payer"
              isDisabled={true}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              control={control}
              controllerName={`practiceRequestInfo.${"insurancePayer3"}`}
              label="#3 Insurance Payer"
              isDisabled={true}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid>
        </Grid> */}
      </DialogContent>
      <Box sx={{ ...footerSx, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onClose}
          sx={cancelBtnSx}
        >
          Back
        </Button>
      </Box>
    </Dialog>
  );
};

export default PracticeApprovePopup;
